import {Location} from '@angular/common';
import {Injectable, OnDestroy} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';

declare const ga: any;

/**
 * Lightweight Google Analytics wrapper for page views and events.
 *
 * Subscribes to Angular router navigation events when enabled and sends
 * pageview hits. Also exposes a helper to send custom events.
 */
@Injectable()
export class AnalyticsService implements OnDestroy {

  /** Signals subscription teardown to avoid memory leaks. */
  protected destroy$ = new Subject<boolean>();

  /** Whether analytics collection is enabled. */
  private readonly enabled: boolean;

  /**
   * Creates the service.
   * @param location Angular `Location` service for current path.
   * @param router Angular router to observe navigation events.
   */
  constructor(private location: Location, private router: Router) {
    this.enabled = false;
  }

  /** Cleans up internal subscriptions. */
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  /**
   * Begins tracking page views on router navigation when enabled.
   */
  trackPageViews() {
    if (this.enabled) {
      this.router.events
        .pipe(
          takeUntil(this.destroy$),
          filter((event) => event instanceof NavigationEnd),
        )
        .subscribe(() => {
          ga('send', {hitType: 'pageview', page: this.location.path()});
        });
    }
  }

  /**
   * Sends a custom GA event when enabled.
   * @param eventName The event action/name to record.
   */
  trackEvent(eventName: string) {
    if (this.enabled) {
      ga('send', 'event', eventName);
    }
  }
}
